import { DropDownProps, Dropdown } from 'antd';
import React from 'react';
import { getLocale, setLocale, getAllLocales } from '@umijs/max';

export interface HeaderDropdownProps extends DropDownProps {
  overlayClassName?: string;
  placement?:
    | 'bottomLeft'
    | 'bottomRight'
    | 'topLeft'
    | 'topCenter'
    | 'topRight'
    | 'bottomCenter';
}

const HeaderDropdown: React.FC<HeaderDropdownProps> = ({ overlayClassName: cls, ...restProps }) => (
  <Dropdown overlayClassName={cls} {...restProps} />
);

interface LocalData {
  lang: string;
  label: string;
  icon: string;
  title: string;
}

interface SelectLangProps {
  globalIconClassName?: string;
  postLocalesData?: (locales: LocalData[]) => LocalData[];
  onItemClick?: (params: any) => void;
  className?: string;
  reload?: boolean;
  icon?: React.ReactNode;
  style?: React.CSSProperties;
}

const SelectLang: React.FC<SelectLangProps> = (props) => {
  const {
    globalIconClassName,
    postLocalesData,
    onItemClick,
    style,
    reload,
    icon,
    ...restProps
  } = props;

  const selectedLang = getLocale() || 'vi-VN';
  const locales = getAllLocales() || [];

  const langLabels: Record<string, string> = {
    'bn-BD': 'Tiếng Indonesia',
    'en-US': 'Tiếng Anh', 
    'fa-IR': 'Tiếng Thái',
    'id-ID': 'Tiếng Lào',
    'ja-JP': 'Tiếng Campuchia',
    'pt-BR': 'Tiếng Bồ Đào Nha',
    'vi-VN': 'Tiếng Việt',
    'zh-CN': 'Tiếng Trung',
    'zh-TW': 'Tiếng Nhật',
  };

  const langFlags: Record<string, string> = {
    'bn-BD': '🇧🇩',
    'en-US': '🇺🇸', 
    'fa-IR': '🇮🇷',
    'id-ID': '🇮🇩',
    'ja-JP': '🇯🇵',
    'pt-BR': '🇧🇷',
    'vi-VN': '🇻🇳',
    'zh-CN': '🇨🇳',
    'zh-TW': '🇹🇼',
  };

  const changeLang = ({ key }: any) => {
    setLocale(key, reload);
    if (onItemClick) {
      onItemClick({ key });
    }
  };

  const allLangUIConfig: LocalData[] = locales.map((locale: string) => {
    return {
      lang: locale,
      label: langLabels[locale] || locale,
      icon: langFlags[locale] || '�️',
      title: langLabels[locale] || locale,
    };
  });

  // Custom sorting: vi-VN first, then en-US, zh-CN, zh-TW, then alphabetical
  let finalLangUIConfig: LocalData[] = allLangUIConfig.sort((a: LocalData, b: LocalData) => {
    const priority = { 'vi-VN': 1, 'en-US': 2, 'zh-CN': 3, 'fa-IR': 4, 'id-ID' : 5, 'ja-JP' : 6, 'pt-BR' : 7 };
    const aPriority = priority[a.lang as keyof typeof priority] || 999;
    const bPriority = priority[b.lang as keyof typeof priority] || 999;
    
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }
    
    return a.label.localeCompare(b.label);
  });

  // Apply postLocalesData if provided
  if (postLocalesData) {
    finalLangUIConfig = postLocalesData(finalLangUIConfig);
  }

  const handleClick = onItemClick ? (params: any) => onItemClick(params) : changeLang;

  const menuItemStyle = { minWidth: "160px" };
  const menuItemIconStyle = { marginRight: "8px" };

  const langMenu = {
    selectedKeys: [selectedLang],
    onClick: handleClick,
    items: finalLangUIConfig.map((localeObj: LocalData) => ({
      key: localeObj.lang,
      style: menuItemStyle,
      label: (
        <>
          <span role="img" aria-label={localeObj.label} style={menuItemIconStyle}>
            {localeObj.icon}
          </span>
          {localeObj.label}
        </>
      ),
    })),
  };

  const inlineStyle = {
    cursor: "pointer",
    padding: "12px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    verticalAlign: "middle",
    ...style,
  };

  return (
    <HeaderDropdown menu={langMenu} placement="bottomRight" {...restProps}>
      <span className={globalIconClassName} style={inlineStyle}>
        <i className="anticon" title={finalLangUIConfig.find((cfg: LocalData) => cfg.lang === selectedLang)?.title}>
          {icon ? icon : (
            <span style={{ fontSize: '18px' }}>
              {langFlags[selectedLang] || '🏳️'}
            </span>
          )}
        </i>
      </span>
    </HeaderDropdown>
  );
};

export default SelectLang;
